/**
 * PaymentDiscountsAdmin.tsx — Admin > Payment Discounts.
 *
 * The account's payment discounts, laid out like every Admin list (Lead
 * Sources is the reference — components/admin/AdminList.tsx) with the corp
 * dashboard's Payment Discounts columns, less Account: Title, Type, Amount,
 * each sorting from its title (by title to start).
 *   - "Add Discount" top right; search by title and Show deleted in the
 *     toolbar (corp's only other filter is Account, which one account has no
 *     use for);
 *   - clicking a live row opens its edit form; Edit / Delete icons on a live
 *     row, Restore on a deleted one; delete asks first;
 *   - the account's "Member Price" discount (type 4) is the row corp marks
 *     "System": the API refuses to change or delete it, so it offers no
 *     actions and does not open.
 * On a Locked page (useAdminPageLevel) there is no Add, no row icon and no
 * Actions column; a live discount still opens, as a viewer with a single Close.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Plus, AlertTriangle } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormField';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { OutlineBadge } from '@/components/admin/CorpBadges';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareNumber, compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { toast } from '@/store/toastStore';
import * as paymentsSvc from '@/services/payments.service';
import type { PaymentDiscount } from '@/services/payments.service';

// ─── Constants ─────────────────────────────────────────────────────────────────

const DISCOUNT_TYPE_LABELS: Record<number, string> = {
  1: 'Flat Amount',
  2: 'Percent',
  3: 'Manual Entry',
  4: 'Member Price',
};

/** The account's Member Price row: a system discount the API will not change or delete. */
const SYSTEM_DISCOUNT_TYPE = 4;

const DISCOUNT_TYPE_OPTIONS = [
  { value: '1', label: 'Flat Amount' },
  { value: '2', label: 'Percent' },
  { value: '3', label: 'Manual Entry' },
];

// The corp Payment Discounts columns (less Account), in corp order.
const COLUMNS: AdminListColumn[] = [
  { key: 'title', header: 'Title', sortable: true },
  { key: 'type', header: 'Type', className: 'w-[15%]', sortable: true },
  { key: 'amount', header: 'Amount', className: 'w-[12%]', sortable: true },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions (nor the System mark that explains their absence), so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

/** Title is the list's own order (as corp); ties keep it. */
const SORT_COMPARE: ListSortCompare<PaymentDiscount> = {
  title: (a, b) => (a.title ?? '').localeCompare(b.title ?? '', undefined, { sensitivity: 'base' }),
  type: (a, b) => compareText(DISCOUNT_TYPE_LABELS[a.discountType] ?? 'Unknown', DISCOUNT_TYPE_LABELS[b.discountType] ?? 'Unknown'),
  // By the number shown; Manual Entry (no amount) first.
  amount: (a, b) =>
    Number(b.discountType === 3) - Number(a.discountType === 3) || compareNumber(a.discountAmount, b.discountAmount),
};

function formatAmount(type: number, amount: number): string {
  if (type === 3) return 'Manual';
  if (type === 2) return `${amount}%`;
  return `$${Number(amount).toFixed(2)}`;
}

// ─── Form state ────────────────────────────────────────────────────────────────

interface DiscountForm {
  title: string;
  discountType: string;
  discountAmount: string;
}

const EMPTY_FORM: DiscountForm = {
  title: '',
  discountType: '1',
  discountAmount: '',
};

// ─── Modal ─────────────────────────────────────────────────────────────────────

interface DiscountModalProps {
  discount?: PaymentDiscount | null;
  onClose: () => void;
  onSaved: (saved: PaymentDiscount) => void;
}

function DiscountModal({ discount, onClose, onSaved }: DiscountModalProps) {
  const isEdit = discount != null;
  // Locked: a viewer — fields read-only, no warnings, Close only.
  const locked = useAdminPageLevel() === 'locked';

  const [form, setForm] = useState<DiscountForm>(() =>
    isEdit
      ? {
          title: discount.title,
          discountType: String(discount.discountType),
          discountAmount: discount.discountType === 3 ? '' : String(discount.discountAmount),
        }
      : EMPTY_FORM,
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isManual = form.discountType === '3';

  function set<K extends keyof DiscountForm>(field: K, value: DiscountForm[K]) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  const warnings = useMemo(() => {
    const msgs: string[] = [];
    const amt = parseFloat(form.discountAmount);
    if (form.discountType === '2' && !isNaN(amt) && amt > 100) {
      msgs.push('Percent discount is greater than 100%');
    }
    if (!isManual && (!form.discountAmount || form.discountAmount === '0')) {
      msgs.push('Discount amount is zero');
    }
    return msgs;
  }, [form, isManual]);

  function handleTypeChange(val: string | null) {
    if (!val) return;
    setForm((f) => ({
      ...f,
      discountType: val,
      discountAmount: val === '3' ? '0' : f.discountAmount,
    }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim() || locked) return;

    const payload = {
      title: form.title.trim(),
      discountType: Number(form.discountType),
      discountAmount: isManual ? 0 : Number(form.discountAmount) || 0,
    };

    setSaving(true);
    setError(null);
    try {
      let saved: PaymentDiscount;
      if (isEdit) {
        saved = await paymentsSvc.updateDiscount(discount.paymentDiscountID, payload);
      } else {
        saved = await paymentsSvc.createDiscount(payload);
      }
      toast.success(isEdit ? 'Discount updated' : 'Discount created');
      onSaved(saved);
      onClose();
    } catch {
      setError('Failed to save discount. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={locked ? 'Discount' : isEdit ? 'Edit Discount' : 'Add Discount'}
      size="md"
      footer={
        locked ? (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={saving || !form.title.trim()}
            loading={saving}
          >
            {isEdit ? 'Save Changes' : 'Add Discount'}
          </Button>
        )
      }
    >
      <AdminLockedFieldset>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}

          <FormField label="Title" required={!locked}>
            <Input
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="e.g. 10% Off"
              required
              autoFocus
            />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Type">
              <Select
                options={DISCOUNT_TYPE_OPTIONS}
                value={form.discountType}
                onChange={(val) => handleTypeChange(val)}
              />
            </FormField>

            <FormField label="Amount">
              <Input
                value={form.discountAmount}
                onChange={(e) => set('discountAmount', e.target.value)}
                placeholder={isManual ? 'N/A' : form.discountType === '2' ? 'Percent value' : 'Dollar amount'}
                disabled={isManual}
              />
              {isManual && (
                <p className="text-xs text-slate-400 mt-1">User enters amount manually</p>
              )}
            </FormField>
          </div>

          {warnings.length > 0 && !locked && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
              <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-700">
                {warnings.map((w, i) => (
                  <p key={i}>{w}</p>
                ))}
              </div>
            </div>
          )}
        </form>
      </AdminLockedFieldset>
    </Modal>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────────

function isDeleted(d: PaymentDiscount): boolean {
  return !d.active;
}

function isSystemDiscount(d: PaymentDiscount): boolean {
  return Number(d.discountType) === SYSTEM_DISCOUNT_TYPE;
}

// ─── Main component ────────────────────────────────────────────────────────────

export function PaymentDiscountsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [allItems, setAllItems] = useState<PaymentDiscount[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState<PaymentDiscount | null>(null);
  const [deleteItem, setDeleteItem] = useState<PaymentDiscount | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await paymentsSvc.listDiscounts();
      setAllItems(data ?? []);
    } catch {
      toast.error('Failed to load discounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Corp searches by title and lists by title.
  const visibleItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return allItems
      .filter((d) => showDeleted || !isDeleted(d))
      .filter((d) => !q || (d.title ?? '').toLowerCase().includes(q))
      .sort(SORT_COMPARE.title);
  }, [allItems, searchQuery, showDeleted]);

  const sorting = useListSort(visibleItems, { initialKey: 'title', compare: SORT_COMPARE });
  const pagination = useListPagination(sorting.sorted, { resetOn: [searchQuery, showDeleted] });

  function handleCreated(saved: PaymentDiscount) {
    setAllItems((prev) => [...prev, saved]);
  }

  function handleUpdated(saved: PaymentDiscount) {
    setAllItems((prev) =>
      prev.map((d) => (d.paymentDiscountID === saved.paymentDiscountID ? { ...d, ...saved } : d)),
    );
    setEditItem(null);
  }

  async function handleDelete() {
    if (!deleteItem) return;
    try {
      await paymentsSvc.deleteDiscount(deleteItem.paymentDiscountID);
      setAllItems((prev) =>
        prev.map((d) =>
          d.paymentDiscountID === deleteItem.paymentDiscountID ? { ...d, active: false } : d,
        ),
      );
      toast.success('Discount deleted');
    } catch {
      toast.error('Failed to delete discount');
    }
    setDeleteItem(null);
  }

  async function handleRestore(item: PaymentDiscount) {
    try {
      await paymentsSvc.restoreDiscount(item.paymentDiscountID);
      setAllItems((prev) =>
        prev.map((d) =>
          d.paymentDiscountID === item.paymentDiscountID ? { ...d, active: true } : d,
        ),
      );
      toast.success('Discount restored');
    } catch {
      toast.error('Failed to restore discount');
    }
  }

  return (
    <AdminListPage
      title="Payment Discounts"
      subtitle="Manage discounts available for payments."
      actions={
        <Button onClick={() => setShowCreate(true)} leftIcon={<Plus className="h-4 w-4" />}>
          Add Discount
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search discounts…"
          searchLabel="Search payment discounts"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="payment-discounts-show-deleted"
        />
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading discounts…" />
      ) : visibleItems.length === 0 ? (
        <AdminListEmpty
          title="No payment discounts found"
          hint={searchQuery.trim() ? 'Try adjusting your search.' : locked ? undefined : 'Add your first discount to get started.'}
        />
      ) : (
        <>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="Payment discounts" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((row) => {
              const deleted = isDeleted(row);
              const system = isSystemDiscount(row);
              // A live row opens its form; a deleted one is restored first, and
              // the system Member Price row cannot be changed at all.
              const opens = !deleted && !system;
              return (
                <tr
                  key={row.paymentDiscountID}
                  className={adminRowClass({ deleted, clickable: opens })}
                  onClick={opens ? () => setEditItem(row) : undefined}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowName deleted={deleted} nameTestId="payment-discount-title">
                      {row.title}
                    </AdminRowName>
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    <span className={`text-sm ${deleted ? 'text-slate-400' : 'text-slate-500'}`}>
                      {DISCOUNT_TYPE_LABELS[row.discountType] ?? 'Unknown'}
                    </span>
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    <span className={`text-sm ${deleted ? 'text-slate-400' : 'text-slate-700'}`}>
                      {formatAmount(row.discountType, row.discountAmount)}
                    </span>
                  </td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {system ? (
                          <OutlineBadge>System</OutlineBadge>
                        ) : deleted ? (
                          <AdminRowAction
                            kind="restore"
                            label={`Restore ${row.title}`}
                            onClick={() => void handleRestore(row)}
                          />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${row.title}`} onClick={() => setEditItem(row)} />
                            <AdminRowAction kind="delete" label={`Delete ${row.title}`} onClick={() => setDeleteItem(row)} />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['discount', 'discounts']} />
        </>
      )}

      {showCreate && (
        <DiscountModal
          onClose={() => setShowCreate(false)}
          onSaved={handleCreated}
        />
      )}

      {editItem && (
        <DiscountModal
          discount={editItem}
          onClose={() => setEditItem(null)}
          onSaved={handleUpdated}
        />
      )}

      <ConfirmDialog
        isOpen={deleteItem !== null}
        title="Delete Discount"
        message={
          deleteItem
            ? `Are you sure you want to delete "${deleteItem.title}"?`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteItem(null)}
      />
    </AdminListPage>
  );
}
